// BlueprintOutlineSection — the MY BLUEPRINT rail's list of what is in the graph.
//
// Round-2 UI note: "'My blueprint' section doesn't show all the nodes in the
// graph. jumps to the first one when we need to be able to find any of that
// node."
//
// The rail already listed node INSTANCES, so "jumps to the first one" was not a
// navigation bug — every row already carried its own node id. It was a LABELLING
// bug: each row rendered its type's palette label, so three `inputKey` nodes were
// three identical rows reading "Input Key" and only the first was ever worth
// clicking. Two more gaps compounded it: only event-typed nodes were listed at
// all, and only at top level, so anything inside a graphGroup or a function was
// absent from the rail entirely.
//
// So: group by type, state the count, expand to per-instance rows that
// `nodeLabel` tells apart, and name the container a nested node lives in.
//
// A group of ONE renders expanded. The count is what earns a disclosure; making
// a single node cost an extra click to reach would trade one usability report
// for another.
//
// Presentational on purpose — the caller supplies already-grouped rows from
// `listNodesByType` and an `onFocus`. That keeps the traversal in one tested
// place (src/lib/graphNodeSearch.js) and lets this render under jsdom with no
// ReactFlow, no graph state and no editor.

import { useState } from 'react'

// Category coding, not theming: the rail's marker matches the node category's
// own accent, from the SAME constant the palette entries use. Both live in
// src/components/nodes/, which is the functional-color exemption in
// tests/unit/paletteEnforcement.test.mjs — so the rail cannot drift to a second
// copy of "the events red". Everything else here is a --m-* token.
import { EVENTS_ACCENT } from '../nodes/eventsTheme'

const ROW = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '5px 12px', cursor: 'pointer',
  borderBottom: '1px solid var(--m-line)',
}

const hoverable = {
  onMouseEnter: (e) => { e.currentTarget.style.background = 'var(--m-panel-hi)' },
  onMouseLeave: (e) => { e.currentTarget.style.background = 'transparent' },
}

/**
 * @param {object}   props
 * @param {string}   props.label          section heading, e.g. "EVENTS"
 * @param {Array}    props.groups         from `listNodesByType` — [{type, count, nodes}]
 * @param {function} props.labelForType   type -> the palette's human label
 * @param {function} props.onFocus        nodeId -> navigate to it
 * @param {string}   [props.emptyHint]    shown instead of an empty box
 * @param {string}   [props.glyph]        row marker
 * @param {string}   [props.accent]       marker colour — a node-category accent
 */
export default function BlueprintOutlineSection({
  label, groups, labelForType, onFocus,
  emptyHint = 'Nothing here yet.',
  glyph = '▶', accent = EVENTS_ACCENT,
}) {
  // Which type rows are open. Absent means "use the default for its size",
  // so a graph that grows a second node of a type does not silently stay
  // collapsed on a stale `true` from when it had one.
  const [open, setOpen] = useState({})
  const isOpen = (g) => open[g.type] ?? g.count === 1

  if (!groups?.length) {
    return (
      <div style={{ color: 'var(--m-fg-3)', padding: '6px 12px', fontSize: 10, fontStyle: 'italic' }}>
        {emptyHint}
      </div>
    )
  }

  const sorted = [...groups].sort((a, b) =>
    String(labelForType(a.type)).localeCompare(String(labelForType(b.type))))

  return (
    <>
      {sorted.map((g) => {
        const shown = isOpen(g)
        return (
          <div key={g.type}>
            <div
              data-testid={`outline-group-${g.type}`}
              data-count={g.count}
              onClick={() => setOpen((o) => ({ ...o, [g.type]: !shown }))}
              style={ROW}
              {...hoverable}
            >
              <span style={{ color: 'var(--m-fg-3)', fontSize: 8, width: 8 }}>{shown ? '▼' : '▶'}</span>
              <span style={{ color: accent, fontSize: 11 }}>{glyph}</span>
              <span style={{ flex: 1, color: 'var(--m-fg-2)', fontSize: 10 }}>{labelForType(g.type)}</span>
              {/* The count is the whole point: without it there is no sign a
                  second copy exists, which is what the report describes. */}
              <span style={{ color: 'var(--m-fg-3)', fontSize: 9, fontFamily: 'var(--m-mono)' }}>{g.count}</span>
            </div>

            {shown && g.nodes.map((n) => (
              <div
                key={n.nodeId}
                data-testid={`outline-node-${n.nodeId}`}
                title={n.container ? `${n.label} — in ${n.container.name}` : n.label}
                onClick={() => onFocus?.(n.nodeId)}
                style={{ ...ROW, paddingLeft: 30, borderBottomColor: 'var(--m-line)' }}
                {...hoverable}
              >
                <span style={{ color: 'var(--m-fg-3)', fontSize: 9 }}>•</span>
                <span style={{
                  flex: 1, color: 'var(--m-fg-2)', fontSize: 10,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{n.label}</span>
                {/* A node inside a group or function is reachable now, but only
                    if the rail says where it is — otherwise clicking it drills
                    somewhere the user did not know they were going. */}
                {n.container && (
                  <span style={{
                    color: 'var(--m-fg-3)', fontSize: 9, maxWidth: 70,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{n.container.name}</span>
                )}
              </div>
            ))}
          </div>
        )
      })}
    </>
  )
}
